import { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import type { CSSProperties, ReactNode, ChangeEvent } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Mention from '@tiptap/extension-mention';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import { uploadFile, humanSize } from './storage';
import AttachmentPreview from './AttachmentPreview';
import { useTeam } from './api';
import { useDialog } from '../ui/Dialogs';
import { useAuth } from '../auth/AuthProvider';
import type { PendingAttachment, Profile } from './types';

// Extiende Image para declarar `data-storage-path` como atributo nativo del
// nodo. Sin esto, los attrs custom se pierden al pasar por la schema de
// ProseMirror al guardar o re-abrir el mensaje.
const ImageWithStoragePath = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-storage-path': {
        default: null,
        parseHTML: (el) => el.getAttribute('data-storage-path'),
        renderHTML: (attrs) => {
          const path = attrs['data-storage-path'];
          if (!path) return {};
          return { 'data-storage-path': path };
        },
      },
    };
  },
});

// Mention que persiste `data-mention-id` (user_id) y se pinta como chip
// `<span class="mention" data-mention-id="...">@Nombre</span>`. El trigger
// SQL `sync_mentions` lee ese atributo para crear filas en `public.mentions`.
const MentionWithUserId = Mention.extend({
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-mention-id'),
        renderHTML: (attrs) => (attrs.id ? { 'data-mention-id': attrs.id } : {}),
      },
      label: {
        default: null,
        parseHTML: (el) => el.textContent?.replace(/^@/, '') ?? null,
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span.mention[data-mention-id]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const id = node.attrs.id as string | null;
    const label = (node.attrs.label as string | null) ?? id ?? '';
    return [
      'span',
      { ...HTMLAttributes, class: 'mention', 'data-mention-id': id },
      `@${label}`,
    ];
  },
});

interface MentionSuggestionItem extends Profile {}

interface ToolbarBtnProps {
  active?: boolean;
  onClick: () => void;
  title?: string;
  children: ReactNode;
  disabled?: boolean;
}

const ToolbarBtn = ({ active, onClick, title, children, disabled }: ToolbarBtnProps) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    disabled={disabled}
    style={{
      background: active ? '#0f172a' : 'transparent',
      color: active ? '#fff' : '#475569',
      border: '1px solid #e2e8f0',
      borderRadius: 6,
      padding: '4px 9px',
      fontSize: 13,
      fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer',
      lineHeight: 1,
      opacity: disabled ? 0.4 : 1,
    }}
  >
    {children}
  </button>
);

export interface MessageEditorHandle {
  getContent: () => { body: string; attachments: PendingAttachment[] };
  clear: () => void;
  isEmpty: () => boolean;
  isUploading: () => boolean;
}

export interface MessageEditorProps {
  taskId: string;
  onSend: (data: { body: string; attachments: PendingAttachment[] }) => Promise<void> | void;
  disabled?: boolean;
  placeholder?: string;
  showSendButton?: boolean;
  minHeight?: number;
  initialContent?: string;
}

const MessageEditor = forwardRef<MessageEditorHandle, MessageEditorProps>(function MessageEditor(
  {
    taskId,
    onSend,
    disabled,
    placeholder = 'Escribe un mensaje…',
    showSendButton = true,
    minHeight = 60,
    initialContent = '',
  },
  ref,
) {
  const { alert, prompt } = useDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  // Equipo para el popup de @menciones. Lo guardamos en un ref vivo porque
  // la suggestion config se captura una sola vez en useEditor.
  const { team } = useTeam();
  const teamRef = useRef<Profile[]>([]);
  useEffect(() => { teamRef.current = team; }, [team]);

  // Espacio activo (por pestaña) para prefijar los paths de Storage.
  const { activeTeamId } = useAuth();
  const teamId = activeTeamId ?? '';

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      ImageWithStoragePath.configure({ HTMLAttributes: { style: 'max-width:100%; border-radius:8px;' } }),
      MentionWithUserId.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: {
          char: '@',
          items: ({ query }: { query: string }) => {
            const q = query.toLowerCase();
            return teamRef.current
              .filter((p) => p.nombre.toLowerCase().includes(q))
              .slice(0, 8);
          },
          render: () => {
            let popup: TippyInstance[] | null = null;
            let element: HTMLDivElement | null = null;
            let currentItems: MentionSuggestionItem[] = [];
            let currentCommand: ((p: { id: string; label: string }) => void) | null = null;
            let selectedIndex = 0;

            const draw = () => {
              if (!element) return;
              element.innerHTML = '';
              if (currentItems.length === 0) {
                const empty = document.createElement('div');
                empty.className = 'mention-popup__empty';
                empty.textContent = 'Sin coincidencias';
                element.appendChild(empty);
                return;
              }
              currentItems.forEach((p, idx) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = `mention-popup__item${idx === selectedIndex ? ' is-selected' : ''}`;
                const dot = document.createElement('span');
                dot.className = 'mention-popup__dot';
                dot.style.background = p.color;
                const name = document.createElement('span');
                name.textContent = p.nombre;
                btn.appendChild(dot);
                btn.appendChild(name);
                btn.addEventListener('mousedown', (e) => {
                  e.preventDefault();
                  select(idx);
                });
                element!.appendChild(btn);
              });
            };

            const select = (idx: number) => {
              const item = currentItems[idx];
              if (!item || !currentCommand) return;
              currentCommand({ id: item.id, label: item.nombre });
            };

            return {
              onStart: (props: any) => {
                currentItems = (props.items ?? []) as MentionSuggestionItem[];
                currentCommand = props.command;
                selectedIndex = 0;
                element = document.createElement('div');
                element.className = 'mention-popup';
                draw();
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },
              onUpdate: (props: any) => {
                currentItems = (props.items ?? []) as MentionSuggestionItem[];
                currentCommand = props.command;
                selectedIndex = 0;
                draw();
                popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
              },
              onKeyDown: (props: any) => {
                const ev: KeyboardEvent = props.event;
                if (ev.key === 'Escape') {
                  popup?.[0]?.hide();
                  return true;
                }
                if (currentItems.length === 0) return false;
                if (ev.key === 'ArrowUp') {
                  selectedIndex = (selectedIndex + currentItems.length - 1) % currentItems.length;
                  draw();
                  return true;
                }
                if (ev.key === 'ArrowDown') {
                  selectedIndex = (selectedIndex + 1) % currentItems.length;
                  draw();
                  return true;
                }
                if (ev.key === 'Enter') {
                  select(selectedIndex);
                  return true;
                }
                return false;
              },
              onExit: () => {
                popup?.[0]?.destroy();
                popup = null;
                element = null;
              },
            };
          },
        },
      }),
    ],
    content: initialContent,
    editorProps: {
      attributes: { style: `min-height:${minHeight}px; outline:none; padding:10px 14px; font-size:14px; line-height:1.5;` },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items || [];
        for (const item of items) {
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              insertImage(file);
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  const insertImage = useCallback(async (file: File) => {
    if (!editor) return;
    setUploading(true);
    try {
      const { storagePath } = await uploadFile(file, { taskId, teamId });
      const previewUrl = URL.createObjectURL(file);
      editor.chain().focus().insertContent({
        type: 'image',
        attrs: {
          src: previewUrl,
          alt: file.name,
          'data-storage-path': storagePath,
        },
      }).run();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'Error subiendo imagen', message: msg });
    } finally {
      setUploading(false);
    }
  }, [editor, taskId, teamId]);

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: PendingAttachment[] = [];
      for (const file of files) {
        const { storagePath } = await uploadFile(file, { taskId, teamId });
        uploaded.push({
          storage_path: storagePath,
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
        });
      }
      setPendingAttachments((cur) => [...cur, ...uploaded]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'Error subiendo archivo', message: msg });
    } finally {
      setUploading(false);
    }
  };

  const removePending = (idx: number) =>
    setPendingAttachments((cur) => cur.filter((_, i) => i !== idx));

  const insertLink = async () => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href || '';
    const url = await prompt({
      title: 'Insertar enlace',
      message: 'Pega la URL del enlace. Déjalo vacío para quitarlo.',
      defaultValue: previous,
      placeholder: 'https://…',
      confirmLabel: 'Aplicar',
    });
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const isEmpty = (): boolean =>
    !editor || (editor.isEmpty && pendingAttachments.length === 0);

  const getContent = (): { body: string; attachments: PendingAttachment[] } => ({
    body: editor ? editor.getHTML() : '',
    attachments: pendingAttachments,
  });

  const clear = () => {
    if (editor) editor.commands.clearContent();
    setPendingAttachments([]);
  };

  useImperativeHandle(
    ref,
    () => ({ getContent, clear, isEmpty, isUploading: () => uploading }),
    [editor, pendingAttachments, uploading],
  );

  const handleSend = async () => {
    if (!editor) return;
    if (isEmpty()) return;
    const { body, attachments } = getContent();
    setSending(true);
    try {
      await onSend({ body, attachments });
      clear();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      alert({ title: 'Error enviando', message: msg });
    } finally {
      setSending(false);
    }
  };

  if (!editor) return null;

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <ToolbarBtn
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Negrita (Cmd+B)"
        ><b>B</b></ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Subrayado (Cmd+U)"
        ><u>U</u></ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Cursiva (Cmd+I)"
        ><i>I</i></ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Lista con viñetas"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" />
            <circle cx="4.5" cy="6" r="1.3" fill="currentColor" stroke="none" /><circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none" /><circle cx="4.5" cy="18" r="1.3" fill="currentColor" stroke="none" />
          </svg>
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Lista numerada"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <line x1="10" y1="6" x2="20" y2="6" /><line x1="10" y1="12" x2="20" y2="12" /><line x1="10" y1="18" x2="20" y2="18" />
            <text x="1" y="8.5" fontSize="8.5" fontWeight="700" fill="currentColor" stroke="none">1</text>
            <text x="1" y="20.5" fontSize="8.5" fontWeight="700" fill="currentColor" stroke="none">2</text>
          </svg>
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('link')}
          onClick={insertLink}
          title="Insertar enlace"
        >🔗</ToolbarBtn>
        <ToolbarBtn
          onClick={handleAttachClick}
          title="Adjuntar archivo"
          disabled={uploading}
        >📎</ToolbarBtn>
        {showSendButton && (
          <span style={styles.hint}>⌘/Ctrl + Enter para enviar</span>
        )}
      </div>

      <div
        style={{ opacity: disabled ? 0.5 : 1 }}
        onKeyDown={(e) => {
          if (showSendButton && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleSend();
          }
        }}
      >
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>

      {pendingAttachments.length > 0 && (
        <div style={styles.attachmentsRow}>
          {pendingAttachments.map((att, idx) => (
            <AttachmentPreview
              key={idx}
              fileName={att.file_name}
              size={humanSize(att.size_bytes)}
              onRemove={() => removePending(idx)}
            />
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFiles}
        style={{ display: 'none' }}
      />

      {showSendButton && (
        <div style={styles.actionsRow}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            {uploading ? 'Subiendo archivos…' : ''}
          </span>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || uploading || disabled}
            style={styles.sendBtn}
          >
            {sending ? '...' : 'Enviar'}
          </button>
        </div>
      )}
      {!showSendButton && uploading && (
        <div style={{ padding: '6px 12px', fontSize: 12, color: '#94a3b8' }}>Subiendo archivos…</div>
      )}
    </div>
  );
});

const styles: Record<string, CSSProperties> = {
  wrap: {
    border: '1px solid #cbd5e1',
    borderRadius: 12,
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 8px',
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
  },
  hint: {
    marginLeft: 'auto',
    fontSize: 11,
    color: '#94a3b8',
  },
  attachmentsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    padding: '8px 10px',
    borderTop: '1px solid #f1f5f9',
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    borderTop: '1px solid #f1f5f9',
  },
  sendBtn: {
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 20px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default MessageEditor;
